//dependencies
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import React from "react";

import { faGripDotsVertical, faMinus, faPlus } from "@performyard/fontawesome/pro-regular-svg-icons";
import { faSparkles } from "@performyard/fontawesome/pro-solid-svg-icons";
import {
	ButtonIcon,
	ButtonStandard,
	Checkbox,
	FormField,
	FormHandle,
	FormSubscribe,
	PYForm,
	ResetButton,
	SubmitButton,
	Text,
	TextEditor,
	Tooltip,
	usePyForm,
} from "@performyard/py-components";
import type { FormQuestionType } from "@performyard/service-contracts";

import { FormQuestionRephraseModule } from "../../../../../../components/AIModules/FormQuestionRephraseModule";
//components
import { Divider } from "../../../../../../components/Divider";
import Icon from "../../../../../../components/Icon/Icon";
import SelectInput from "../../../../../../components/Inputs/SelectInput";

import { useAiSettings } from "../../../../../../api/hooks";

import { QUESTION_TYPES_CAN_BE_REQUIRED, ReviewQuestion, ReviewQuestionSchema } from "../../utils";

//hooks
import { useEditFormStore } from "../../useEditFormStore";
import { questionDisplayMap } from "../QuestionBlock";

const questionOptions: { value: string; label: string }[] = [
	{ value: "text", label: "Long Answer" },
	{ value: "multiline", label: "Multiple Line Answer" },
	{ value: "numeric", label: "Numeric Answer" },
	{ value: "date", label: "Date" },
	{ value: "multiple-choice", label: "Multiple Choice" },
	{ value: "multiple-select", label: "Multiple Choice" },
	{ value: "instructions", label: "Instructions" },
	{ value: "rating-scale", label: "Rating Scale" },
	{ value: "goal-discussion", label: "Goal Discussion" },
	{ value: "goal-creation", label: "Goal Creation" },
	{ value: "calculator", label: "Numeric Calculation" },
];

const filterByUniqueLabel = (options: { value: string; label: string }[]) => {
	const seen = new Set<string>();
	return options.filter((option) => {
		if (seen.has(option.label)) return false;
		seen.add(option.label);
		return true;
	});
};

const uniqueByLabel = filterByUniqueLabel(questionOptions);

const mapQuestionTypeToFormQuestionType = (type: string | undefined): FormQuestionType => {
	const typeMap: Record<string, FormQuestionType> = {
		text: "Long answer",
		multiline: "Multi-line answer",
		numeric: "Numeric",
		date: "Date",
		"multiple-choice": "Multiple choice",
		"multiple-select": "Multiple choice",
		instructions: "Instructions",
		"rating-scale": "Rating scale",
		"goal-discussion": "Goal discussion",
		"goal-creation": "Goal creation",
		calculator: "Numeric calculation",
	};
	return typeMap[type || "text"] || "Long answer";
};

type AIRephraseButtonProps = {
	value: string;
	questionType: FormQuestionType;
	onInsert: (newValue: string) => void;
};

const AIRephraseButton = ({ value, questionType, onInsert }: AIRephraseButtonProps): React.ReactElement | null => {
	const { data: aiSettings } = useAiSettings();
	const [isOpen, setIsOpen] = React.useState(false);

	const stringValue = typeof value === "string" ? value : "";
	const isEnabled = aiSettings?.formQuestionAssistantEnabled && stringValue.trim();

	if (!isEnabled) {
		return null;
	}

	const handleOpen = (e: React.MouseEvent): void => {
		e.stopPropagation();
		e.preventDefault();
		setIsOpen(true);
	};

	const handleClose = (): void => {
		setIsOpen(false);
	};

	const handleInsert = (newValue: string): void => {
		const coercedValue = Array.isArray(newValue) ? newValue.join("") : String(newValue ?? "");
		onInsert(coercedValue);
	};

	return (
		<div
			className="relative flex-none"
			onClick={(e) => e.stopPropagation()}
			onMouseDown={(e) => e.stopPropagation()}
		>
			<Tooltip content="Rephrase" placement="top" size="small" asChild>
				<ButtonIcon
					icon={faSparkles}
					size={20}
					variant="ghost"
					color="primary"
					shade={5}
					onClick={handleOpen}
					aria-label="Rephrase with AI"
					data-testid="ai-rephrase-button"
					dangerouslySetClassName="hover:bg-primary-1"
				/>
			</Tooltip>
			{isOpen && (
				<FormQuestionRephraseModule
					input={stringValue}
					questionType={questionType}
					onInsert={handleInsert}
					isOpen={isOpen}
					onOpenChange={(open) => {
						if (!open) handleClose();
					}}
				/>
			)}
		</div>
	);
};

type EditQuestionBlockContentProps = {
	question: ReviewQuestion;
	index: number;
};

const EditQuestionBlockContent = ({ question, index }: EditQuestionBlockContentProps) => {
	const form = usePyForm<typeof ReviewQuestionSchema>();
	const [ref] = useAutoAnimate();

	const {
		setEditingQuestion,
		isNewestQuestion = false,
		deleteQuestion,
	} = useEditFormStore((state) => ({
		setEditingQuestion: state.setEditingQuestion,
		isNewestQuestion: state.editingQuestion?.isNew,
		deleteQuestion: state.deleteQuestion,
	}));

	const handleToggleSubtext = (toggleSubtext: boolean) => {
		form.setFieldValue("subtext", toggleSubtext ? "" : undefined);
	};

	const handleCancel = () => {
		if (isNewestQuestion) {
			deleteQuestion(question.refId);
		}
		setEditingQuestion();
	};

	return (
		<div className="group min-w-0 max-w-full flex-shrink flex-grow break-words">
			<div>
				<div className="flex items-center justify-between bg-neutral-1 pb-2 pl-4 pr-4 pt-2">
					<div className="w-fit">
						<FormSubscribe id="subtext-toggle" selector={(state) => state.values?.subtext}>
							{(subtext) =>
								subtext !== undefined && subtext !== null ? (
									<ButtonStandard
										color="neutral"
										size="medium"
										iconLeft={faMinus}
										variant="outline"
										type="button"
										onClick={() => {
											handleToggleSubtext(false);
										}}
									>
										Remove Subtitle
									</ButtonStandard>
								) : (
									<ButtonStandard
										color="neutral"
										size="medium"
										iconLeft={faPlus}
										variant="outline"
										type="button"
										onClick={() => {
											handleToggleSubtext(true);
										}}
									>
										Add Subtitle
									</ButtonStandard>
								)
							}
						</FormSubscribe>
					</div>
					<div className="flex gap-2">
						<ResetButton
							color="red"
							size="medium"
							onReset={() => {
								handleCancel();
							}}
						>
							Cancel
						</ResetButton>
						<SubmitButton color="primary" size="medium">
							{isNewestQuestion ? "Create" : "Update"}
						</SubmitButton>
					</div>
				</div>
				<div ref={ref} className="flex min-w-0 max-w-full flex-col gap-4 p-4">
					<div className="w-[186px]">
						<FormField<string>
							id="type"
							input={(selectionField) => (
								<SelectInput
									menuPortalTarget={document.body}
									results={uniqueByLabel}
									onChange={(value) => {
										if (value?.value === "rating-scale") {
											form.setFieldValue("type", "rating-scale");
											form.setFieldValue("choices", [{ value: "", name: "", description: "" }]);
										} else if (
											value?.value === "multiple-choice" ||
											value?.value === "multiple-select"
										) {
											form.setFieldValue("type", value?.value);
											form.setFieldValue("choices", [""]);
										} else {
											selectionField.onChange(value?.value);
										}
									}}
									value={{
										value: selectionField.value,
										label: questionDisplayMap[selectionField.value || "text"]?.label,
									}}
								/>
							)}
						/>
					</div>
					<FormField<string>
						id="question"
						validators={{
							onChangeListenTo: ["subtext"],
							onChange: ({ fieldApi }) => {
								fieldApi.state.meta.errors = [];
								fieldApi.state.meta.isTouched = true;
							},
						}}
						label={form.state.values?.type === "instructions" ? "Instruction Title" : "Question Title"}
						input={(textField) => (
							<TextEditor
								id={`questions[${index}].question`}
								initialValue={textField.value || ""}
								onChange={textField.onChange}
								onBlur={textField.onBlur}
								placeholder="Your question title"
								minHeight={32}
								maxHeight={200}
								rightActionElement={
									<AIRephraseButton
										value={textField.value || ""}
										questionType={mapQuestionTypeToFormQuestionType(question?.type)}
										onInsert={textField.onChange}
									/>
								}
							/>
						)}
					/>

					<FormSubscribe
						id="subtext-field"
						selector={(state) => ({ subtext: state.values?.subtext, type: state.values?.type })}
					>
						{({ subtext, type }) =>
							subtext !== undefined && subtext !== null ? (
								<FormField<string>
									id="subtext"
									label={type === "instructions" ? "Instruction Subtitle" : "Question Subtitle"}
									input={(textField) => (
										<TextEditor
											id={`questions[${index}].subtext`}
											initialValue={textField.value || ""}
											onChange={textField.onChange}
											onBlur={textField.onBlur}
											placeholder="Your question subtitle"
											minHeight={32}
											maxHeight={200}
											parseOptions={{
												preserveWhitespace: "full",
											}}
											rightActionElement={
												<AIRephraseButton
													value={textField.value || ""}
													questionType={mapQuestionTypeToFormQuestionType(question?.type)}
													onInsert={textField.onChange}
												/>
											}
										/>
									)}
								/>
							) : null
						}
					</FormSubscribe>

					<FormSubscribe id="question-display" selector={(state) => [state.values]}>
						{([values]) =>
							questionDisplayMap[values.type || "text"]?.edit({
								index,
								question: values,
							})
						}
					</FormSubscribe>

					<FormSubscribe id="question-required" selector={(state) => state.values?.type}>
						{(questionType) =>
							questionType && QUESTION_TYPES_CAN_BE_REQUIRED.includes(questionType) ? (
								<div>
									<Divider />
									<div className="flex w-full justify-end pt-2">
										<div>
											<FormField<boolean>
												id="is_required"
												label={<Text size="paragraph-3">Required Question</Text>}
												labelPosition="right"
												input={(field) => (
													<div className="flex items-center gap-2">
														<Checkbox
															id="is_required"
															checked={field.value}
															onCheckedChange={() => field.onChange(!field.value)}
														/>
													</div>
												)}
											/>
										</div>
									</div>
								</div>
							) : null
						}
					</FormSubscribe>
				</div>
			</div>
		</div>
	);
};

type EditQuestionBlockWrapperProps = {
	question: ReviewQuestion;
	index: number;
	formRef: React.RefObject<FormHandle<typeof ReviewQuestionSchema>>;
	onSubmit: (values: ReviewQuestion) => void;
};

const EditQuestionBlock = ({ question, index, formRef, onSubmit }: EditQuestionBlockWrapperProps) => {
	const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: question.refId || "new" });

	const style = {
		transform: CSS.Translate.toString(transform),
		transition,
	};

	return (
		<div
			ref={setNodeRef}
			style={style}
			{...attributes}
			id={`question-${question?.refId}`}
			className="mb-4 flex items-stretch justify-center border-[1px] border-solid border-neutral-2 bg-neutral-0"
		>
			<div
				{...listeners}
				className="flex w-6 flex-shrink-0 cursor-grab items-center justify-center border-r-[1px] border-solid border-neutral-2 text-neutral-7 hover:text-neutral-9 active:cursor-grabbing"
			>
				<Icon icon={faGripDotsVertical} />
			</div>
			<PYForm
				id={`edit-question-${question?.refId}`}
				ref={formRef}
				schema={ReviewQuestionSchema}
				defaultValues={question}
				onSubmit={({ value }) => onSubmit(value)}
				dangerouslySetClassName="flex-grow min-w-0"
			>
				<EditQuestionBlockContent question={question} index={index} />
			</PYForm>
		</div>
	);
};

export default EditQuestionBlock;
